import axios from 'axios';
import storage, { sessionStorage } from './storage';
import { API_HOST } from '../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { addBreadcrumb } from './errorMonitoring';

const AUTH_RELOAD_KEY = 'bt-auth-reload';

let isReloadingDueToAuth = false;

function handleUnauthorized() {
    if (isReloadingDueToAuth) return true;

    storage.removeItem('token');

    if (sessionStorage.getItem(AUTH_RELOAD_KEY) === 'true') {
        return false;
    }

    isReloadingDueToAuth = true;
    sessionStorage.setItem(AUTH_RELOAD_KEY, 'true');
    console.warn('Unauthorized (401) detected, reloading the page to refresh session');
    window.location.reload();
    return true;
}

async function getToken() {
    const storageToken = storage.getItem('token') ?? null;

    try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) {
            return storageToken;
        }

        if (storageToken !== token) {
            storage.setItem('token', token);
        }
        return token;
    } catch (e) {
        return storageToken;
    }
}

const tokenInjection = async (config) => {
    const accessToken = await getToken();

    if (accessToken) {
        config.headers = { ...config.headers, Authorization: `Bearer ${accessToken}` };
    }

    config._startTime = Date.now();
    return config;
};

const responseSuccessInterceptor = (response) => {
    sessionStorage.removeItem(AUTH_RELOAD_KEY);

    const duration = response.config._startTime ? Date.now() - response.config._startTime : undefined;
    addBreadcrumb(`${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`, 'api', {
        method: response.config.method,
        url: response.config.url,
        status: response.status,
        duration,
    });
    return response;
};

const responseErrorInterceptor = (error) => {
    const { config, response } = error;
    const duration = config?._startTime ? Date.now() - config._startTime : undefined;

    addBreadcrumb(
        `API Error: ${config?.method?.toUpperCase()} ${config?.url} - ${response?.status || 'No response'}`,
        'api-error',
        {
            method: config?.method,
            url: config?.url,
            status: response?.status,
            statusText: response?.statusText,
            errorDescription: response?.data?.description,
            errorCode: error.code,
            errorName: error.name,
            duration,
        },
    );

    if (response?.status === 401) {
        if (handleUnauthorized()) {
            return new Promise(() => {});
        }
    }

    return Promise.reject(error);
};

const axiosInstance = axios.create({ baseURL: API_HOST });

axiosInstance.interceptors.request.use(tokenInjection);
axiosInstance.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);

export default axiosInstance;
